import React from 'react';
import {
  Box,
  Button,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { customList } from 'country-codes-list';
import { Header } from '..';
import { customStyles } from '../../Utils/utils';
import { cardiacTokens as T } from '../../theme/cardiacTheme';

/* Shared shell + form pieces for My Profile and Clinic Profile, so both screens
   look identical. Presentation only; each page keeps its own logic. */

export const profileInputSx = {
  '& .MuiOutlinedInput-root': {
    background: T.surface,
    borderRadius: '10px',
    color: T.ink,
    fontSize: 14,
  },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: T.line },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: T.lineStrong },
  '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: T.teal },
  '& .MuiInputBase-input': { padding: '12px 14px' },
  '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: T.ink3 },
  '& .Mui-disabled .MuiOutlinedInput-notchedOutline': { borderColor: T.line },
  '& .MuiFormHelperText-root': { color: T.red, ml: '2px', fontSize: 12 },
} as const;

const menuPaperSx = {
  maxHeight: 320,
  background: T.surface2,
  border: `1px solid ${T.lineStrong}`,
  '& .MuiMenuItem-root': {
    fontSize: 13,
    color: T.ink2,
    '&:hover': { background: 'rgba(45,212,191,0.12)', color: T.teal },
  },
};

export const ProfileLabel: React.FC<{ children: React.ReactNode; required?: boolean }> = ({
  children,
  required,
}) => (
  <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.ink2, mb: '8px' }}>
    {children}
    {required && (
      <Box
        component="span"
        sx={{ color: T.red, ml: '2px' }}
      >
        *
      </Box>
    )}
  </Typography>
);

/** Label + control + optional hint, one grid cell. */
export const ProfileField: React.FC<{
  label: string;
  required?: boolean;
  hint?: React.ReactNode;
  children: React.ReactNode;
}> = ({ label, required, hint, children }) => (
  <Box>
    <ProfileLabel required={required}>{label}</ProfileLabel>
    {children}
    {hint && <Box sx={{ mt: '6px', fontSize: 12, color: T.ink3 }}>{hint}</Box>}
  </Box>
);

/** Two-column (one on mobile) grid for fields. */
export const ProfileGrid: React.FC<{ children: React.ReactNode; columns?: string }> = ({
  children,
  columns,
}) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', md: columns || '1fr 1fr' },
      columnGap: '22px',
      rowGap: '20px',
    }}
  >
    {children}
  </Box>
);

/** Titled card section; `action` sits on the right of the title. */
export const ProfileSection: React.FC<{
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, action, children }) => (
  <Box
    sx={{
      background: `linear-gradient(180deg, ${T.surface2}, ${T.surface})`,
      border: `1px solid ${T.line}`,
      borderRadius: '16px',
      p: { xs: '18px', md: '26px' },
    }}
  >
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      flexWrap="wrap"
      gap={1.5}
      sx={{ mb: '22px' }}
    >
      <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>{title}</Typography>
      {action}
    </Stack>
    {children}
  </Box>
);

/** Page chrome: layout background + app header. Put the Formik form inside. */
export const ProfilePageShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <section
    className="layoutContainer"
    style={customStyles.layoutBackground}
  >
    <div className="layoutGrid">
      <div className="page">
        <Header />
        <div className="page__body">{children}</div>
      </div>
    </div>
  </section>
);

/** Padded content column; render inside the <Form>. */
export const ProfileContent: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
      px: { xs: '18px', md: '30px' },
      py: '22px',
      color: T.ink,
      display: 'flex',
      flexDirection: 'column',
      gap: '20px',
    }}
  >
    {children}
  </Box>
);

/** Title row with Cancel / Update. Must be inside the <Form> (Update submits it). */
export const ProfileHead: React.FC<{
  title: string;
  onCancel: () => void;
  submitDisabled?: boolean;
}> = ({ title, onCancel, submitDisabled }) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="space-between"
    flexWrap="wrap"
    gap={2}
  >
    <Typography
      component="h1"
      sx={{ fontSize: 22, fontWeight: 700, color: '#fff' }}
    >
      {title}
    </Typography>
    <Stack
      direction="row"
      gap={1.5}
    >
      <Button
        type="button"
        onClick={onCancel}
        variant="outlined"
        sx={{
          color: T.ink,
          borderColor: T.lineStrong,
          borderRadius: '10px',
          px: '18px',
          py: '9px',
          fontWeight: 600,
          '&:hover': { borderColor: T.teal, color: T.teal, background: 'transparent' },
        }}
      >
        Cancel
      </Button>
      <Button
        type="submit"
        disabled={submitDisabled}
        variant="contained"
        sx={{
          color: '#04221d',
          fontWeight: 700,
          borderRadius: '10px',
          px: '20px',
          py: '9px',
          background: `linear-gradient(92deg, ${T.sky}, ${T.teal})`,
          boxShadow: '0 6px 16px -7px rgba(45,212,191,0.7)',
          '&:hover': {
            filter: 'brightness(1.08)',
            background: `linear-gradient(92deg, ${T.sky}, ${T.teal})`,
          },
          '&.Mui-disabled': { opacity: 0.5, color: '#04221d' },
        }}
      >
        Update
      </Button>
    </Stack>
  </Stack>
);

/** Dropdown in the shared field style. Options are plain {value,label}. */
export const ProfileSelect: React.FC<{
  name?: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  disabled?: boolean;
  placeholder?: string;
}> = ({ name, value, onChange, options, disabled, placeholder = '-Select-' }) => (
  <Select
    fullWidth
    name={name}
    value={value ?? ''}
    onChange={e => onChange(String(e.target.value))}
    displayEmpty
    disabled={disabled}
    MenuProps={{ PaperProps: { sx: menuPaperSx } }}
    sx={profileInputSx}
  >
    <MenuItem value="">{placeholder}</MenuItem>
    {options.map(o => (
      <MenuItem
        key={o.value}
        value={o.value}
      >
        {o.label}
      </MenuItem>
    ))}
  </Select>
);

/** Unique calling codes, sorted; "+07" style padding kept from the old screens. */
export const useCountryCallingCodes = () =>
  React.useMemo(() => {
    const countryCodes = customList('countryCode', '{countryCallingCode}');
    return Array.from(
      new Map(
        Object.entries(countryCodes)
          .sort((a, b) => Number(a[1]) - Number(b[1]))
          .map(([code, callingCode]) => [callingCode, code])
      )
    ).map(([callingCode, code]) => ({ code, callingCode: callingCode.padStart(2, '0') }));
  }, []);

/** Phone number with the country-code select inside the field. */
export const ProfilePhoneField: React.FC<{
  name: string;
  value: string;
  countryCode: string;
  onCountryCodeChange: (code: string) => void;
  onChange: (digits: string) => void;
  onBlur: React.FocusEventHandler;
  error?: string;
}> = ({ name, value, countryCode, onCountryCodeChange, onChange, onBlur, error }) => {
  const codes = useCountryCallingCodes();
  return (
    <TextField
      fullWidth
      name={name}
      placeholder="Contact Number"
      value={value || ''}
      onChange={e => onChange(e.target.value.replace(/\D/g, ''))}
      onBlur={onBlur}
      error={!!error}
      helperText={error || ''}
      inputProps={{ maxLength: 10, inputMode: 'numeric' }}
      InputProps={{
        startAdornment: (
          <InputAdornment
            position="start"
            sx={{ mr: 0 }}
          >
            <Select
              value={countryCode}
              onChange={e => onCountryCodeChange(String(e.target.value))}
              variant="standard"
              disableUnderline
              MenuProps={{ PaperProps: { sx: menuPaperSx } }}
              sx={{
                color: T.ink,
                fontSize: 14,
                fontWeight: 600,
                mr: '8px',
                pr: '4px',
                borderRight: `1px solid ${T.line}`,
                '& .MuiSelect-select': { pr: '20px !important', py: 0 },
                '& .MuiSelect-icon': { color: T.ink3 },
              }}
            >
              {codes.map(({ callingCode }) => (
                <MenuItem
                  key={callingCode}
                  value={`+${callingCode}`}
                >
                  +{callingCode}
                </MenuItem>
              ))}
            </Select>
          </InputAdornment>
        ),
      }}
      sx={profileInputSx}
    />
  );
};
